import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
	loadImportedFilesTableData,
	refreshAllFilePks,
	setPaginationAndCounters,
} from '../file-table/table-state/import-files-table.actions';
import { combineLatest, firstValueFrom } from 'rxjs';
import { FileListControlFacade } from './file-list-control.facade';
import {
	fileSessionInfo,
	selectFilterOptions,
	selectPaginationOptions,
} from '../file-table/table-state/import-files-table.selectors';
import { FilesRepoService } from '../services/files-repo.service';
import { CmmsTableSelectionService } from '@app/pages/cmms/services/cmms-table-selection.service';
import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';

@Injectable({
	providedIn: 'root',
})
export class FilesRepoFacade {
	//  Abstraction for grouping the actions related the responsibility of loading files
	private readonly store$ = inject(Store);
	private readonly filesRepoService = inject(FilesRepoService);
	private readonly fileListFacade = inject(FileListControlFacade);
	private readonly cmmsSelectionService = inject(CmmsTableSelectionService<IFileEntity>);
	constructor() {}

	// This method should only be used on new page, deletion, completion marking and on initial load
	loadImportedFiles() {
		this.store$.dispatch(loadImportedFilesTableData());
		this.store$.dispatch(refreshAllFilePks());
	}

	// TODO: Investigate if we can do bulk deletion without loading the files again
	deleteImportedFiles(pks: string[]) {
		firstValueFrom(this.filesRepoService.deleteFiles(pks)).then(success => {
			if (!success) {
				return;
			}
			this.loadImportedFiles();
		});
	}

	// TODO: Make this fetch the files directly by id when the BE has a filter for pks for files
	silentlyUpdateFiles() {
		this.queryFilesWithCurrentOptions().then(queryResult => {
			const files = queryResult.files;
			this.store$.dispatch(
				setPaginationAndCounters({
					filesPagination: queryResult.filesPagination,
					fileImportCounters: queryResult.fileImportCounters,
				})
			);
			this.fileListFacade.set(files);
			const selectionModel = this.cmmsSelectionService.getSelections();
			const updatedSelectionPks = selectionModel.selected.map(f => f.pk);
			const newSelections = files.filter(f => updatedSelectionPks.includes(f.pk));
			selectionModel.clear();
			selectionModel.select(...newSelections);
		});
	}

	private async queryFilesWithCurrentOptions() {
		const [sessionInfo, filterOptions, paginationOptions] = await firstValueFrom(
			combineLatest([
				this.store$.select(fileSessionInfo),
				this.store$.select(selectFilterOptions),
				this.store$.select(selectPaginationOptions),
			])
		);
		return await firstValueFrom(
			this.filesRepoService.filesQuery(sessionInfo.pk, {
				...filterOptions,
				...paginationOptions,
			})
		);
	}
}
